"use client";
import { ContentfulImageConfig, TypeHeroWithoutUnresolvableLinksResponse } from "@2u-landing-pages/types";
import cn from "classnames";
import React from "react";

import styles from "./TemplateWrapper.module.scss";

interface TemplateWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  as?: "section" | "div" | "header" | "footer";
  backgroundColor?: string;
  padded?: boolean;
  drawer?: boolean;
  paddingTop?: string;
  paddingBottom?: string;
  backgroundImage?: TypeHeroWithoutUnresolvableLinksResponse["fields"]["background_image"];
  backgroundImagePosition?:
    | "top-left"
    | "top-center"
    | "top-right"
    | "left-center"
    | "center-center"
    | "right-center"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right";
  image_config?: ContentfulImageConfig;
}

interface CustomCSSProperties extends React.CSSProperties {
  "--surface-background-color"?: string;
  "--background-image-desktop"?: string;
  "--background-image-mobile"?: string;
  "--background-image-position"?: string;
}

type PaddingMap = { [index: string]: string };

// Map the contentful string values to values used in the css classes
const paddingMap: PaddingMap = {
  "Large (64/32)": "large",
  "Medium (48/24)": "medium",
  "Small (32/16)": "small",
  "X-Small (24/12)": "xsmall",
};

export const TemplateWrapper: React.FC<TemplateWrapperProps> = ({
  as = "section",
  children,
  className,
  padded,
  paddingTop = "Large (64/32)",
  paddingBottom = "Large (64/32)",
  backgroundColor,
  backgroundImage,
  backgroundImagePosition,
  image_config,
  style: styleProp,
  drawer,
  ...rest
}) => {
  let style: CustomCSSProperties = {
    ...styleProp,
  };

  if (backgroundColor) style["--surface-background-color"] = `var(${backgroundColor})`;

  if (backgroundImage?.fields?.file?.url) {
    const baseUrl = `https:${backgroundImage.fields.file.url}`;
    let desktopParams = "q=70&fm=avif&w=1920";
    let mobileParams = "q=70&fm=avif&w=1024&fit=fill&f=center";
    const imgCfg = image_config as unknown as ContentfulImageConfig;

    if (imgCfg) {
      if (imgCfg?.default) {
        const defaultConfig = {
          ...Object.fromEntries(new URLSearchParams(desktopParams)),
          ...imgCfg.default,
          // TODO: Change the custom Image Plugin image type selection to a single value; An array of values is not supported by Contentful Image API
          ...(imgCfg.default.fm && { fm: imgCfg.default.fm[0] }),
        };
        desktopParams = new URLSearchParams(defaultConfig as unknown as Record<string, string>).toString();
      }
      if (imgCfg?.mobile) {
        const mobileConfig = {
          ...Object.fromEntries(new URLSearchParams(mobileParams)),
          ...imgCfg.mobile,
          // TODO: Change the custom Image Plugin image type selection to a single value; An array of values is not supported by Contentful Image API
          ...(imgCfg.mobile.fm && { fm: imgCfg.mobile.fm[0] }),
        };
        mobileParams = new URLSearchParams(mobileConfig as unknown as Record<string, string>).toString();
      }
    }
    style["--background-image-desktop"] = `url(${baseUrl}?${desktopParams})`;
    style["--background-image-mobile"] = `url(${baseUrl}?${mobileParams})`;
    style["--background-image-position"] = `${backgroundImagePosition?.replace("-", " ") ?? "center"}`;
  }

  const padding = "medium";

  return React.createElement(
    as,
    {
      className: cn(styles.templateWrapper, className, {
        [styles.padded]: padded,
        [styles[`padding-top-${paddingMap[paddingTop]}`]]: padded,
        [styles[`padding-bottom-${paddingMap[paddingBottom]}`]]: padded,
        [styles.drawer]: drawer,
      }),
      style,
      ...rest,
    },
    children
  );
};
